'use client';

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { SlideOver } from '@/components/admin/SlideOver';
import { storefrontHref } from '@/components/platform/format';
import { themeLabel } from '@/lib/storefront/theme-document';
import { AgencyManagePanel } from '@/components/platform/AgencyManagePanel';
import { OpenDesk } from '@/components/platform/OpenDesk';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, ACCOUNT_PASSWORD_MAX, ACCOUNT_PASSWORD_MIN, leaveIfSignedOut } from '@/lib/platform/accounts';
import { filterTenantDirectory, parseTenantDetail, type PlatformTenantRow, type TenantHealthFilter } from '@/lib/platform/summary';
import { usePlatformSummary } from '@/pages/platform/usePlatformSummary';

const SEARCH_DEBOUNCE_MS = 200;
const HEALTH_OPTIONS: { value: TenantHealthFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Connected' },
  { value: 'unreachable', label: 'Unreachable' },
];

export function AgenciesPage() {
  const navigate = useNavigate();
  const { overview, error, reload } = usePlatformSummary('Could not load the agency directory.');
  const [text, setText] = useState('');
  const query = useDebouncedValue(text, SEARCH_DEBOUNCE_MS);
  const [status, setStatus] = useState('all');
  const [plan, setPlan] = useState('all');
  const [health, setHealth] = useState<TenantHealthFilter>('all');
  const [managed, setManaged] = useState<PlatformTenantRow | null>(null);
  const [opening, setOpening] = useState<PlatformTenantRow | null>(null);
  const [pausing, setPausing] = useState<PlatformTenantRow | null>(null);
  const [pausePending, setPausePending] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);
  const [resetting, setResetting] = useState<PlatformTenantRow | null>(null);

  const rows = overview?.tenants ?? [];
  const visible = useMemo(
    () => filterTenantDirectory(rows, { text: query, status, plan, health }),
    [rows, query, status, plan, health],
  );
  const filtersActive = text !== '' || status !== 'all' || plan !== 'all' || health !== 'all';

  async function changeStatus() {
    if (!pausing) return;
    const next = pausing.status === 'paused' ? 'active' : 'paused';
    setPausePending(true);
    setPauseError(null);
    try {
      await api(`/api/platform/tenants/${pausing.slug}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      notify(next === 'paused' ? 'Agency paused.' : 'Agency resumed.');
      setPausing(null);
      reload();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setPauseError(accountError(err, 'Could not update this agency.'));
    } finally {
      setPausePending(false);
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="type-section text-ink">Agencies</h2>
          <p className="text-sm text-muted">Registry and live infrastructure monitoring across all tenant instances.</p>
        </div>
        <Link to="/super-admin/agencies/new" className="rounded-full bg-brand px-3 py-1.5 text-center text-xs font-semibold text-slate-950">
          New agency
        </Link>
      </div>
      {overview && <KpiStrip tenants={overview.tenants} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <label className="text-xs font-medium text-muted sm:col-span-2">
          Search
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Name, city, or slug"
            className="mt-1 w-full rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
          />
        </label>
        <Filter label="Status" value={status} onChange={setStatus} options={['all', 'active', 'paused']} />
        <Filter label="Plan" value={plan} onChange={setPlan} options={planChoices(overview?.tenants ?? [])} />
        <label className="text-xs font-medium text-muted">
          Database
          <select value={health} onChange={(event) => setHealth(readHealth(event.target.value))} className="mt-1 w-full rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand">
            {HEALTH_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>
      {filtersActive && (
        <button type="button" onClick={() => { setText(''); setStatus('all'); setPlan('all'); setHealth('all'); }} className="text-xs font-semibold text-muted hover:text-ink">
          Clear filters
        </button>
      )}
      {error && (
        <div className="rounded-2xl border border-danger/40 bg-card px-4 py-6">
          <p className="text-sm text-danger">{error}</p>
          <button type="button" onClick={reload} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Try again</button>
        </div>
      )}
      {overview === null && !error && <div className="h-40 animate-pulse rounded-2xl bg-card" aria-hidden="true" />}
      {overview?.tenants.length === 0 && <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No agencies are registered yet.</p>}
      {overview && overview.tenants.length > 0 && visible.length === 0 && <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No agencies match that search.</p>}
      {visible.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-line bg-card">
          <table className="min-w-full text-left text-sm">
            <thead className="type-table-header text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Agency</th>
                <th className="px-4 py-3 font-medium">Status and billing</th>
                <th className="px-4 py-3 font-medium">Plan and fleet</th>
                <th className="px-4 py-3 font-medium">Theme</th>
                <th className="px-4 py-3 font-medium">Database</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((tenant) => (
                <AgencyRow
                  key={tenant.slug}
                  tenant={tenant}
                  onManage={() => setManaged(tenant)}
                  onEnter={() => setOpening(tenant)}
                  onPause={() => { setPauseError(null); setPausing(tenant); }}
                  onReset={() => setResetting(tenant)}
                  onRepaired={reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {managed && (
        <SlideOver open title={managed.name} onClose={() => setManaged(null)} panelClassName="max-w-2xl">
          <AgencyManagePanel tenant={managed} onChanged={reload} onEnterDesk={() => setOpening(managed)} />
        </SlideOver>
      )}
      {opening && (
        <OpenDesk
          open
          agencyName={opening.name}
          slug={opening.slug}
          onClose={() => setOpening(null)}
          onStarted={() => navigate('/admin')}
        />
      )}
      <ConfirmDialog
        open={pausing !== null}
        title={pausing?.status === 'paused' ? `Resume ${pausing.name}?` : `Pause ${pausing?.name ?? 'this agency'}?`}
        description={pausing?.status === 'paused'
          ? 'The public site and the desk accept requests again.'
          : 'The public site and the desk stop on the next request. Bookings already stored stay in that database.'}
        confirmLabel={pausing?.status === 'paused' ? 'Resume agency' : 'Pause agency'}
        pending={pausePending}
        error={pauseError}
        onCancel={() => { if (!pausePending) setPausing(null); }}
        onConfirm={() => void changeStatus()}
      />
      {resetting && <ResetOwner slug={resetting.slug} name={resetting.name} onClose={() => setResetting(null)} />}
    </section>
  );
}

function AgencyRow({
  tenant,
  onManage,
  onEnter,
  onPause,
  onReset,
  onRepaired,
}: {
  tenant: PlatformTenantRow;
  onManage: () => void;
  onEnter: () => void;
  onPause: () => void;
  onReset: () => void;
  onRepaired: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const href = storefrontHref(tenant);

  async function fixDatabase() {
    setFixing(true);
    setFixError(null);
    try {
      await api(`/api/platform/tenants/${tenant.slug}/migrate`, { method: 'POST', body: '{}' });
      notify('Schema repair requested.');
      onRepaired();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setFixError(accountError(err, 'Could not repair this database.'));
    } finally {
      setFixing(false);
    }
  }

  async function backup() {
    setMenu(false);
    try {
      await api(`/api/platform/tenants/${tenant.slug}/backup`, { method: 'POST', body: '{}' });
      notify('Database archived.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not archive this database.'), 'error');
    }
  }

  return (
    <tr className="border-t border-line">
      <td className="type-table-cell px-4 py-3">
        <Link to={`/super-admin/agencies/${tenant.slug}`} className="font-medium text-ink hover:underline">{tenant.name}</Link>
        <p className="text-xs text-muted">{tenant.city}</p>
        <a href={href} target="_blank" rel="noreferrer" className="text-xs text-muted hover:text-ink">{href} ↗</a>
      </td>
      <td className="px-4 py-3">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(tenant.status)}`}>{label(tenant.status)}</span>
        <span className={`ms-2 rounded-full px-2 py-0.5 text-xs font-semibold ${billingClass(tenant.billingStatus)}`}>{label(tenant.billingStatus)}</span>
      </td>
      <td className="px-4 py-3 text-ink">
        <span className="capitalize">{tenant.subscriptionTier}</span>
        <p className="text-xs text-muted">{fleetLabel(tenant)}</p>
      </td>
      <td className="px-4 py-3">
        <span className="rounded-full bg-shell px-2 py-0.5 text-xs font-semibold text-ink">{themeLabel(tenant.design)}</span>
      </td>
      <td className="px-4 py-3">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tenant.error ? 'bg-danger/15 text-danger' : 'bg-success/15 text-success'}`}>
          {tenant.error ? 'Unreachable' : 'Connected'}
        </span>
        {tenant.error && <p className="mt-1 max-w-xs text-xs text-danger">{tenant.error}</p>}
        {tenant.error && (
          <button type="button" disabled={fixing} onClick={() => void fixDatabase()} className="ms-2 text-xs font-semibold text-ink underline">
            {fixing ? 'Fixing…' : 'Fix DB'}
          </button>
        )}
        {fixError && <p className="mt-1 text-xs text-danger">{fixError}</p>}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={tenant.error !== null} onClick={onEnter} className="rounded-full bg-brand px-2 py-1 text-xs font-semibold text-slate-950 disabled:opacity-50">Enter desk</button>
          <button type="button" onClick={onManage} className="rounded-full border border-line px-2 py-1 text-xs font-semibold text-ink">Manage</button>
          <div className="relative">
            <button type="button" aria-label="More actions" onClick={() => setMenu((open) => !open)} className="rounded-full border border-line px-2 py-1 text-xs text-ink">···</button>
            {menu && (
              <div className="absolute end-0 z-10 mt-1 w-48 rounded-xl border border-line bg-card p-1 shadow-lg">
                <MenuButton label={tenant.status === 'paused' ? 'Resume tenant' : 'Pause tenant'} onClick={() => { setMenu(false); onPause(); }} />
                <MenuButton label="Reset owner password" onClick={() => { setMenu(false); onReset(); }} />
                <MenuButton label="Back up this database" onClick={() => void backup()} />
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function ResetOwner({ slug, name, onClose }: { slug: string; name: string; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<unknown>(`/api/platform/tenants/${slug}`)
      .then((payload) => {
        const detail = parseTenantDetail(payload);
        const owner = detail.staff.find((member) => member.role === 'super_admin' && member.active) ?? detail.staff.find((member) => member.role === 'super_admin');
        if (!cancelled) setOwnerId(owner?.id ?? null);
        if (!cancelled && owner === undefined) setError(detail.error ?? 'This agency has no owner account.');
      })
      .catch((err: unknown) => {
        if (!cancelled && !leaveIfSignedOut(err)) setError(accountError(err, 'Could not load the owner.'));
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function save() {
    if (!ownerId) return;
    if (password.length < ACCOUNT_PASSWORD_MIN || password.length > ACCOUNT_PASSWORD_MAX) {
      setError(`Password must be between ${ACCOUNT_PASSWORD_MIN} and ${ACCOUNT_PASSWORD_MAX} characters.`);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/staff/${slug}/${ownerId}`, { method: 'PATCH', body: JSON.stringify({ password }) });
      notify('Owner password updated.');
      onClose();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not reset this password.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver
      open
      title={`Reset password for ${name}`}
      onClose={onClose}
      footer={<button type="button" disabled={pending || ownerId === null} onClick={() => void save()} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">{pending ? 'Saving…' : 'Save password'}</button>}
    >
      <p className="text-sm text-muted">The new password is not shown again and is not written to the audit.</p>
      <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" className="mt-3 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink" />
      {error && <p className="mt-2 text-xs font-semibold text-danger">{error}</p>}
    </SlideOver>
  );
}

function KpiStrip({ tenants }: { tenants: PlatformTenantRow[] }) {
  const active = tenants.filter((row) => row.status === 'active').length;
  const fleet = tenants.reduce((sum, row) => sum + (row.vehicles ?? 0), 0);
  const cars = fleet === 1 ? '1 car' : `${fleet} cars`;
  const unreachable = tenants.filter((row) => row.error !== null).length;
  const tiers = planChoices(tenants).filter((tier) => tier !== 'all').map((tier) => `${tenants.filter((row) => row.subscriptionTier === tier).length} ${tier}`).join(' · ');
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Card label="Total agencies" value={String(tenants.length)} />
      <Card label="Active sites / fleets" value={`${active} sites · ${cars}`} />
      <Card label="Database health" value={`${tenants.length - unreachable} operational, ${unreachable} unreachable`} />
      <Card label="Active tiers" value={tiers} />
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-line bg-card px-4 py-3">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className="mt-1 text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

function planChoices(tenants: PlatformTenantRow[]): string[] {
  const tiers = [...new Set(tenants.map((row) => row.subscriptionTier))].sort();
  return ['all', ...(tiers.length > 0 ? tiers : ['starter', 'growth', 'enterprise'])];
}

function Filter({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <label className="text-xs font-medium text-muted">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded-xl border border-line bg-card px-3 py-2 text-sm capitalize text-ink outline-none focus:border-brand">
        {options.map((option) => <option key={option} value={option}>{option === 'all' ? 'All' : option}</option>)}
      </select>
    </label>
  );
}

function MenuButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="block w-full rounded-lg px-3 py-2 text-left text-xs text-ink hover:bg-shell">{label}</button>;
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function fleetLabel(tenant: PlatformTenantRow): string {
  if (tenant.vehicles === null) return 'Fleet unknown';
  return tenant.vehicleLimit === null ? `${tenant.vehicles} / Unlimited` : `${tenant.vehicles} / ${tenant.vehicleLimit} cars`;
}

function label(value: string): string {
  return value.replaceAll('_', ' ');
}

function statusClass(status: string): string {
  if (status === 'paused') return 'bg-warning/15 text-warning';
  if (status === 'active') return 'bg-success/15 text-success';
  return 'bg-shell text-ink';
}

function billingClass(status: string): string {
  if (status === 'past_due') return 'bg-danger/15 text-danger';
  if (status === 'cancelled' || status === 'expired') return 'bg-warning/15 text-warning';
  return 'bg-success/15 text-success';
}

function readHealth(value: string): TenantHealthFilter {
  if (value === 'open' || value === 'unreachable') return value;
  return 'all';
}
